import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { firstValueFrom, timeout } from 'rxjs';
import { JavapiService } from 'src/app/services/javapi.service';
import { MetodosgenService } from 'src/app/services/metodosgen.service';
import { Metodosui } from 'src/app/services/metodosui';
import { Browser } from '@capacitor/browser';
import { WorkerSelectorComponent } from '../worker-selector/worker-selector.component';

// =============================================
// INTERFACES DE TIPADO
// =============================================

/** Representa un destinatario individual (solicitante). */
export interface Destinatario {
    id: string;
    nombre: string;
    correo: string;
    tipo: 'TO' | 'CC'; // Destinatario principal o en copia
    grupoNombre?: string;
}

/** Representa un grupo de solicitantes. */
export interface GrupoCorreo {
    id: string;
    nombre: string;
    cantidad: number;
    correos: string[];
    tipo: 'TO' | 'CC'; // Grupo principal o en copia
}

/** Representa un archivo adjunto. */
export interface AdjuntoCorreo {
    nombre: string;
    tamanio?: number;
    tipo?: string;
    archivo?: File;
    esRutaServidor?: boolean;
    rutaServidor?: string;
    alias?: string;
}

/** Acción del usuario al cerrar el modal. */
export type AccionCorreo = 'GUARDAR' | 'ENVIAR' | null;

// =============================================
// COMPONENTE
// =============================================

@Component({
    selector: 'app-enviarcorreo',
    templateUrl: './enviarcorreo.component.html',
    styleUrls: ['./enviarcorreo.component.scss'],
    standalone: false
})
export class EnviarcorreoComponent implements OnInit {

    @ViewChild('searchDest') searchDest?: ElementRef<HTMLInputElement>;
    @ViewChild('searchGroup') searchGroup?: ElementRef<HTMLInputElement>;

    /** Título que aparece en el header del modal (ej. "Enviar Cotización #123"). */
    @Input() tituloModal: string = 'Nuevo Correo';

    /** Módulo de origen para registrar en la bitácora (ej. 'COTIZACIONES'). */
    @Input() moduloOrigen: string = 'GENERAL';

    /** ID de referencia del registro relacionado (ej. id de la cotización). */
    @Input() idReferencia: string | number = '';

    /** Asunto prellenado opcional. */
    @Input() asuntoPrellenado: string = '';

    /** Objeto con los datos para reemplazar variables en la plantilla ($id, $lugar, etc). */
    @Input() dataSource: any = {};

    /** ID de cotización vinculada (pasado explícitamente desde el componente padre). */
    @Input() cotizacionId: number | string | null = null;

    /** Lista de todos los solicitantes disponibles (se cargan del backend). */
    todosDestinatarios: Destinatario[] = [];

    /** Lista de todos los grupos disponibles (se cargan del backend). */
    todosGrupos: GrupoCorreo[] = [];

    // -- Estado del formulario --
    destinatariosSeleccionados: Destinatario[] = [];
    gruposSeleccionados: GrupoCorreo[] = [];
    adjuntosSeleccionados: AdjuntoCorreo[] = [];
    asunto: string = '';
    cuerpo: string = '';

    // -- Búsqueda --
    busquedaDestinatario: string = '';
    busquedaGrupo: string = '';
    destinatariosFiltrados: Destinatario[] = [];
    gruposFiltrados: GrupoCorreo[] = [];
    mostrarBuscadorDestinatario: boolean = false;
    mostrarBuscadorGrupo: boolean = false;

    // -- Estado de la UI --
    isSaving: boolean = false;
    accionActual: AccionCorreo = null;
    usuarioActual: string = '';

    // -- Borradores e Historial --
    borradores: any[] = [];
    historialEnvios: any[] = [];
    pestanaActiva: 'borradores' | 'historial' = 'borradores';
    cargandoBorradores: boolean = false;
    cargandoHistorial: boolean = false;
    correoDetalleAbiertoId: string | null = null;
    // -- Lógica ARS / INFT --
    tieneArs: boolean = false;
    tieneInft: boolean = false;
    tieneCotizacion: boolean = false;
    rutaArs: string = '';
    rutaInft: string = '';
    rutaCotizacion: string = '';
    adjuntarArs: boolean = false;
    adjuntarInft: boolean = false;
    adjuntarCotizacion: boolean = false;
    aliasRemitente: string = '';

    // -- Lógica SOLICITUD --
    esSolicitud: boolean = false;
    /** Lunes de la semana actualmente visible en el calendario */
    solicitudWeekStart: Date = new Date();
    /** Fecha seleccionada en formato YYYY-MM-DD */
    solicitudFechaISO: string = '';
    /** Texto formateado: "lunes 01 de junio 2026" */
    solicitudFechaTexto: string = '';
    /** Hora seleccionada (1-12) */
    solicitudHora: number = 8;
    /** Minutos seleccionados: '00' | '30' */
    solicitudMinutos: string = '00';
    /** AM o PM */
    solicitudAmPm: string = 'AM';
    /** Ítems de trabajos a realizar */
    solicitudItems: string[] = [];
    /** Input temporal para agregar ítems */
    nuevoItemTrabajo: string = '';
    /** Lista de trabajadores asignados */
    solicitudTrabajadores: any[] = [];
    /** Templates crudos guardados para reprocesar al cambiar controles */
    private rawAsuntoTemplate: string = '';
    private rawCuerpoTemplate: string = '';

    // Nombres de los días cortos
    readonly SOLICITUD_DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    // Opciones de hora
    readonly SOLICITUD_HORAS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    readonly SOLICITUD_MINUTOS = ['00', '30'];
    readonly SOLICITUD_AMPM = ['AM', 'PM'];

    constructor(
        private modalCtrl: ModalController,
        private javapiService: JavapiService,
        private metodosGenService: MetodosgenService,
        private metodosUi: Metodosui
    ) { }

    async ngOnInit(): Promise<void> {
        // Obtener usuario de sesión
        const userJson = localStorage.getItem('user');
        if (userJson) {
            this.usuarioActual = JSON.parse(userJson).l_usuario || 'system';
        }

        // Si viene de un módulo específico con ID, cargar plantilla
        if (this.moduloOrigen !== 'GENERAL' && this.idReferencia) {
            this.cargarPlantilla();
            this.cargarBorradoresRegistro();
            this.cargarHistorialRegistro();
        } else if (this.asuntoPrellenado) {
            this.asunto = this.asuntoPrellenado;
        }

        await Promise.all([
            this.cargarDestinatarios(),
            this.cargarGrupos()
        ]);

        this.preseleccionarSolicitanteYGrupos();
    }

    private preseleccionarSolicitanteYGrupos() {
        if ((this.moduloOrigen === 'ARS' || this.moduloOrigen === 'SOLICITUD' || this.moduloOrigen === 'COTIZACIONES') && this.dataSource?.solicitante) {
            const solicitanteNombre = this.dataSource.solicitante.toLowerCase().trim();
            const dest = this.todosDestinatarios.find(d => d.nombre.toLowerCase().trim() === solicitanteNombre);
            if (dest) {
                if (!this.destinatariosSeleccionados.some(d => d.id === dest.id)) {
                    this.destinatariosSeleccionados = [...this.destinatariosSeleccionados, dest];
                }
                
                if (dest.grupoNombre && dest.grupoNombre !== 'Sin Grupo') {
                    const grupo = this.todosGrupos.find(g => g.nombre.toLowerCase().trim() === dest.grupoNombre!.toLowerCase().trim());
                    if (grupo) {
                        if (!this.gruposSeleccionados.some(g => g.id === grupo.id)) {
                            this.gruposSeleccionados = [...this.gruposSeleccionados, grupo];
                        }
                    }
                }
            }
        }
    }

    // =============================================
    // CARGA DE DATOS
    // =============================================

    /**
     * Carga la plantilla de correo (asunto, cuerpo, adjunto) desde el servidor.
     */
    private async cargarPlantilla(): Promise<void> {
        try {
            const modMap: any = { 'COTIZACIONES': 'COTIZACION', 'TRABAJOS': 'TRABAJO' };
            const sub = modMap[this.moduloOrigen] || this.moduloOrigen;

            const payload = this.metodosGenService.setModeloGeneral(
                'correos', 'GET_TEMPLATE',
                sub, String(this.idReferencia), '', '', '', '', '', '', '', '', '', '', '', '', ''
            );

            // 1. Obtener la respuesta inicial del SP para adjuntos y datos heredados
            const res: any = await firstValueFrom(
                this.javapiService.getCommonModel(payload).pipe(timeout(15000))
            );

            if (Array.isArray(res) && res.length > 0 && !res[0].Error) {
                const p = res[0];
                let rawAsunto = p.dt1 || '';
                let rawCuerpo = p.dt2 || '';
                let toValue = p.dt4 || 'estimado Cliente';

                // 2. Cargar la configuración de plantillas parametrizada desde la BD
                try {
                    const paramRes = await firstValueFrom(
                        this.javapiService.getJsonParam('TP_EMAIL_COTIZACION').pipe(timeout(10000))
                    );

                    if (paramRes && paramRes.pa_json) {
                        const config = JSON.parse(paramRes.pa_json);

                        // Determinar la plantilla del módulo correspondiente
                        const templateKey = sub === 'COTIZACION' ? 'env_cotizaciones' : `env_${sub.toLowerCase()}`;
                        const template = config[templateKey] || config['env_cotizaciones'];

                        if (template) {
                            if (template.asunto) rawAsunto = template.asunto;
                            if (template.cuerpo) rawCuerpo = template.cuerpo;
                            if (template.alias) this.aliasRemitente = template.alias;
                        }

                        // Resolver el destinatario ($to) según la parametrización de c_to
                        const clienteName = this.dataSource?.cliente || '';
                        const cToConfig = config.c_to || {};
                        toValue = cToConfig['default'] || 'estimado Cliente';

                        if (clienteName && typeof clienteName === 'string') {
                            const matchedKey = Object.keys(cToConfig).find(key =>
                                key.toLowerCase().trim() === clienteName.toLowerCase().trim()
                            );
                            if (matchedKey) {
                                toValue = cToConfig[matchedKey];
                            }
                        }
                    }
                } catch (paramErr) {
                    console.error('[EnviarCorreo] Fallo al cargar parámetro dinámico, usando fallback:', paramErr);
                }

                // Cargar parámetro dinámico de nombre de archivos de cotización para control de prefijo
                let prefijoCotizacion = 'PG-COT_';
                try {
                    const paramCt = await firstValueFrom(
                        this.javapiService.getJsonParam('CT_ARCHIVOS').pipe(timeout(10000))
                    );
                    if (paramCt && paramCt.pa_json) {
                        const configCt = JSON.parse(paramCt.pa_json);
                        prefijoCotizacion = configCt.COTIZACION || 'PG-COT_';
                    }
                } catch (err) {
                    console.error('[EnviarCorreo] Error cargando prefijo CT_ARCHIVOS:', err);
                }

                // Extraer el ID de cotización de forma inteligente desde la ruta física si es módulo ARS
                let resolvedCotId = '';
                if (this.moduloOrigen === 'COTIZACIONES') {
                    resolvedCotId = String(this.idReferencia);
                } else {
                    const cotPath = p.dt8 || '';
                    const match = cotPath.match(/(\d+)\.pdf$/);
                    resolvedCotId = match
                        ? match[1]
                        : String(this.cotizacionId || this.dataSource?.cotizacion_id || this.dataSource?.cotizacionId || '');
                }

                // Inyectar el valor final calculado de 'to' en el dataSource y mapear lugar <-> direccion para reemplazos de variables
                this.dataSource = {
                    ...this.dataSource,
                    to: toValue,
                    direccion: this.dataSource?.lugar || this.dataSource?.direccion || '',
                    lugar: this.dataSource?.lugar || this.dataSource?.direccion || '',
                    'cot-id': resolvedCotId,
                    'trab-id': (this.moduloOrigen === 'ARS' || this.moduloOrigen === 'TRABAJOS' || this.moduloOrigen === 'SOLICITUD')
                        ? String(this.idReferencia)
                        : String(this.dataSource?.trabajo_id || this.dataSource?.trabajoId || '')
                };

                // Procesar la plantilla con las variables resueltas
                if (rawAsunto) this.asunto = this.metodosGenService.processTemplate(rawAsunto, this.dataSource);
                if (rawCuerpo) this.cuerpo = this.metodosGenService.processTemplate(rawCuerpo, this.dataSource);

                // Si el módulo es SOLICITUD, guardar templates crudos y activar controles
                if (this.moduloOrigen === 'SOLICITUD') {
                    this.rawAsuntoTemplate = rawAsunto;
                    this.rawCuerpoTemplate = rawCuerpo;
                    this.esSolicitud = true;
                    // Inicializar la fecha con hoy
                    const today = new Date();
                    this.solicitudFechaISO = this.solicitudToLocalISO(today);
                    this.solicitudFechaTexto = this.formatearFechaEspanol(this.solicitudFechaISO);
                    this.solicitudWeekStart = this.getSolicitudMonday(today);
                    this.reprocesarPlantillaSolicitud();
                // Si el módulo origen es ARS, guardar rutas para poder elegirlas en la vista
                } else if (this.moduloOrigen === 'ARS') {
                    this.rutaArs = p.dt3 || '';
                    this.rutaInft = p.dt7 || '';
                    this.rutaCotizacion = p.dt8 || '';
                    this.tieneArs = !!(this.rutaArs && !this.rutaArs.endsWith('/'));
                    this.tieneInft = false;
                    this.tieneCotizacion = !!(this.rutaCotizacion && !this.rutaCotizacion.endsWith('/'));

                    try {
                        const existsRes: any = await firstValueFrom(
                            this.javapiService.genericPost(`api/pg/trabajos/informe-final/exists/${this.idReferencia}`, {})
                        );
                        this.tieneInft = !!(existsRes && existsRes.exists);
                        if (this.tieneInft && existsRes.fileName) {
                            const basePath = this.rutaInft && this.rutaInft.includes('/')
                                ? this.rutaInft.substring(0, this.rutaInft.lastIndexOf('/') + 1)
                                : '/usr/files/pgarcia/informe_final/';
                            this.rutaInft = basePath + existsRes.fileName;
                        }
                    } catch (err) {
                        console.error('[EnviarCorreo] Error checking final report existence', err);
                        this.tieneInft = false;
                    }

                    // Inicializar los adjuntos según las elecciones por defecto
                    this.actualizarAdjuntosArs();
                } else if (p.dt3) {
                    const nombreArchivo = p.dt3.split('/').pop() || 'archivo.pdf';

                    this.adjuntosSeleccionados.push({
                        nombre: nombreArchivo,
                        esRutaServidor: true,
                        rutaServidor: p.dt3,
                        alias: nombreArchivo,
                        tamanio: 0,
                        tipo: 'application/pdf'
                    });
                }
            }
        } catch (e) {
            console.error('[EnviarCorreo] Error cargando plantilla:', e);
        }
    }

    /**
     * Sincroniza la lista de adjuntos seleccionados según los checkboxes de ARS e INFT.
     */
    actualizarAdjuntosArs(): void {
        // Quitar los adjuntos automáticos de servidor anteriores
        this.adjuntosSeleccionados = this.adjuntosSeleccionados.filter(a => !a.esRutaServidor);

        if (this.tieneCotizacion && this.adjuntarCotizacion) {
            const nombre = this.rutaCotizacion.split('/').pop() || 'Cotizacion.pdf';
            this.adjuntosSeleccionados.push({
                nombre,
                esRutaServidor: true,
                rutaServidor: this.rutaCotizacion,
                alias: nombre,
                tamanio: 0,
                tipo: 'application/pdf'
            });
        }
        if (this.tieneArs && this.adjuntarArs) {
            const nombre = this.rutaArs.split('/').pop() || 'Reporte_ARS.pdf';
            this.adjuntosSeleccionados.push({
                nombre,
                esRutaServidor: true,
                rutaServidor: this.rutaArs,
                alias: nombre,
                tamanio: 0,
                tipo: 'application/pdf'
            });
        }
        if (this.tieneInft && this.adjuntarInft) {
            const nombre = this.rutaInft.split('/').pop() || 'Informe_Trabajo_Final.pdf';
            this.adjuntosSeleccionados.push({
                nombre,
                esRutaServidor: true,
                rutaServidor: this.rutaInft,
                alias: nombre,
                tamanio: 0,
                tipo: 'application/pdf'
            });
        }
    }

    /** Alterna la selección del adjunto INFT si está disponible */
    toggleAdjuntoInft(): void {
        if (this.tieneInft) {
            this.adjuntarInft = !this.adjuntarInft;
            this.actualizarAdjuntosArs();
        }
    }

    /** Alterna la selección del adjunto Cotización si está disponible */
    toggleAdjuntoCotizacion(): void {
        if (this.tieneCotizacion) {
            this.adjuntarCotizacion = !this.adjuntarCotizacion;
            this.actualizarAdjuntosArs();
        }
    }

    /**
     * Carga la lista de solicitantes/destinatarios desde la base de datos.
     * Usa el SP sp_correos_gestion con subrutina GET_DESTINATARIOS.
     */
    private async cargarDestinatarios(): Promise<void> {
        try {
            const payload = this.metodosGenService.setModeloGeneral(
                'correos', 'GET_DESTINATARIOS',
                '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
            );
            const res: any = await firstValueFrom(
                this.javapiService.getCommonModel(payload).pipe(timeout(15000))
            );
            if (Array.isArray(res) && !(res[0] as any)?.Error) {
                this.todosDestinatarios = res.map((r: any) => ({
                    id: r.dt1,
                    nombre: r.dt2,
                    correo: r.dt3,
                    grupoNombre: r.dt4,
                    tipo: 'TO'
                }));
            }
        } catch (e) {
            console.error('[EnviarCorreo] Error cargando destinatarios:', e);
        }
    }

    /**
     * Carga la lista de grupos de solicitantes desde la base de datos.
     * Usa el SP sp_correos_gestion con subrutina GET_GRUPOS.
     */
    private async cargarGrupos(): Promise<void> {
        try {
            const payload = this.metodosGenService.setModeloGeneral(
                'correos', 'GET_GRUPOS',
                '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
            );
            const res: any = await firstValueFrom(
                this.javapiService.getCommonModel(payload).pipe(timeout(15000))
            );
            if (Array.isArray(res) && !(res[0] as any)?.Error) {
                this.todosGrupos = res.map((r: any) => ({
                    id: r.dt1,
                    nombre: r.dt2,
                    cantidad: parseInt(r.dt3 || '0'),
                    correos: r.dt4 ? r.dt4.split(',') : [],
                    tipo: 'TO'
                }));
            }
        } catch (e) {
            console.error('[EnviarCorreo] Error cargando grupos:', e);
        }
    }

    /**
     * Carga los borradores del registro operativo actual.
     */
    async cargarBorradoresRegistro(): Promise<void> {
        if (!this.idReferencia) return;
        this.cargandoBorradores = true;
        try {
            const payload = this.metodosGenService.setModeloGeneral(
                'correos', 'GET_BORRADORES_REGISTRO',
                this.moduloOrigen, String(this.idReferencia), '', '', '', '', '', '', '', '', '', '', '', '', ''
            );
            const res: any = await firstValueFrom(
                this.javapiService.getCommonModel(payload).pipe(timeout(10000))
            );
            if (Array.isArray(res) && !(res[0] as any)?.Error) {
                this.borradores = res.map((r: any) => ({
                    id: r.dt1,
                    asunto: r.dt2,
                    cuerpo: r.dt3,
                    destinatariosDetalle: r.dt4 ? JSON.parse(r.dt4) : [],
                    grupos: r.dt5 ? JSON.parse(r.dt5) : [],
                    adjuntos: r.dt6 ? JSON.parse(r.dt6) : [],
                    fecha: r.dt7,
                    usuario: r.dt8,
                    destinatariosCc: r.dt9 ? r.dt9.split(',') : [],
                    metadata: r.dt10 || ''
                }));
            } else {
                this.borradores = [];
            }
        } catch (e) {
            console.error('[EnviarCorreo] Error cargando borradores del registro:', e);
        } finally {
            this.cargandoBorradores = false;
        }
    }

    /**
     * Carga el historial de envíos de correo para el registro operativo actual.
     */
    async cargarHistorialRegistro(): Promise<void> {
        if (!this.idReferencia) return;
        this.cargandoHistorial = true;
        try {
            const payload = this.metodosGenService.setModeloGeneral(
                'correos', 'GET_HISTORIAL_REGISTRO',
                this.moduloOrigen, String(this.idReferencia), '', '', '', '', '', '', '', '', '', '', '', '', ''
            );
            const res: any = await firstValueFrom(
                this.javapiService.getCommonModel(payload).pipe(timeout(10000))
            );
            if (Array.isArray(res) && !(res[0] as any)?.Error) {
                this.historialEnvios = res.map((r: any) => ({
                    id: r.dt1,
                    asunto: r.dt2,
                    cuerpo: (r.dt3 || '').split('\n||SOLICITUD_META||')[0],
                    destinatariosList: r.dt4 ? r.dt4.split(',') : [],
                    destinatariosCcList: r.dt5 ? r.dt5.split(',') : [],
                    fecha: r.dt6,
                    usuario: r.dt7,
                    destinatariosDetalle: r.dt8 ? JSON.parse(r.dt8) : [],
                    grupos: r.dt9 ? JSON.parse(r.dt9) : [],
                    adjuntos: r.dt10 ? JSON.parse(r.dt10) : [],
                    estado: r.dt11 || 'ENVIADO'
                }));
            } else {
                this.historialEnvios = [];
            }
        } catch (e) {
            console.error('[EnviarCorreo] Error cargando historial de envíos:', e);
        } finally {
            this.cargandoHistorial = false;
        }
    }

    /**
     * Aplica el borrador seleccionado restaurándolo en el formulario actual.
     */
    aplicarBorrador(borrador: any): void {
        if (!borrador) return;

        // 1. Restaurar Asunto y Cuerpo
        let cuerpoRaw: string = borrador.cuerpo || '';
        let solicitudMeta: any = null;

        // Intentar parsear metadata de la columna dedicada
        if (borrador.metadata) {
            try {
                solicitudMeta = JSON.parse(borrador.metadata);
            } catch (err) {
                console.error('[EnviarCorreo] Error parsing metadata from column:', err);
            }
        } 
        
        // Fallback: intentar extraer metadata del cuerpo por retrocompatibilidad con borradores anteriores
        if (!solicitudMeta) {
            const META_SEPARATOR = '\n||SOLICITUD_META||';
            const metaIdx = cuerpoRaw.lastIndexOf(META_SEPARATOR);
            if (metaIdx !== -1) {
                try {
                    solicitudMeta = JSON.parse(cuerpoRaw.slice(metaIdx + META_SEPARATOR.length));
                    cuerpoRaw = cuerpoRaw.slice(0, metaIdx);
                } catch {
                    solicitudMeta = null;
                }
            }
        }

        // Si el cuerpo tiene la tabla HTML inyectada de trabajadores, la reemplazamos por $tabla_personal para que el input visible del editor se mantenga limpio
        if (this.esSolicitud && cuerpoRaw.includes('<table') && cuerpoRaw.includes('</table>')) {
            const startIdx = cuerpoRaw.indexOf('<table');
            const endIdx = cuerpoRaw.indexOf('</table>');
            if (startIdx !== -1 && endIdx !== -1) {
                cuerpoRaw = cuerpoRaw.substring(0, startIdx) + '$tabla_personal' + cuerpoRaw.substring(endIdx + 8);
            }
        }

        this.asunto = borrador.asunto || '';
        this.cuerpo = cuerpoRaw;

        // Restaurar estado de SOLICITUD si hay metadata
        if (solicitudMeta && this.esSolicitud) {
            this.solicitudItems = solicitudMeta.items || [];
            this.solicitudTrabajadores = solicitudMeta.trabajadores || [];
            if (solicitudMeta.fecha) {
                this.solicitudFechaISO = solicitudMeta.fecha;
                this.solicitudFechaTexto = this.formatearFechaEspanol(solicitudMeta.fecha);
                const parts = solicitudMeta.fecha.split('-');
                if (parts.length === 3) {
                    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                    this.solicitudWeekStart = this.getSolicitudMonday(d);
                }
            }
            if (solicitudMeta.hora) this.solicitudHora = solicitudMeta.hora;
            if (solicitudMeta.minutos) this.solicitudMinutos = solicitudMeta.minutos;
            if (solicitudMeta.ampm) this.solicitudAmPm = solicitudMeta.ampm;
        }

        // 2. Restaurar Destinatarios
        if (Array.isArray(borrador.destinatariosDetalle)) {
            this.destinatariosSeleccionados = borrador.destinatariosDetalle.map((d: any) => {
                const esCc = borrador.destinatariosCc?.includes(d.correo);
                return {
                    id: d.id,
                    nombre: d.nombre,
                    correo: d.correo,
                    tipo: esCc ? 'CC' : 'TO'
                };
            });
        } else {
            this.destinatariosSeleccionados = [];
        }

        // 3. Restaurar Grupos
        if (Array.isArray(borrador.grupos)) {
            this.gruposSeleccionados = borrador.grupos.map((bg: any) => {
                const grupoOriginal = this.todosGrupos.find(g => g.id === bg.id);
                return {
                    id: bg.id,
                    nombre: bg.nombre,
                    cantidad: bg.cantidad,
                    correos: grupoOriginal ? grupoOriginal.correos : [],
                    tipo: bg.tipo || 'TO'
                };
            });
        } else {
            this.gruposSeleccionados = [];
        }

        // 4. Restaurar Adjuntos
        if (Array.isArray(borrador.adjuntos)) {
            this.adjuntosSeleccionados = borrador.adjuntos.map((adj: any) => {
                const esServidor = adj.nombre ? (adj.nombre.startsWith('/') || adj.nombre.includes('\\') || adj.nombre.includes('http')) : false;
                const nombreArchivo = adj.alias || (adj.nombre ? adj.nombre.split('/').pop() : 'archivo.pdf') || 'archivo.pdf';
                return {
                    nombre: nombreArchivo,
                    esRutaServidor: esServidor,
                    rutaServidor: esServidor ? adj.nombre : undefined,
                    alias: adj.alias || nombreArchivo,
                    tamanio: 0,
                    tipo: 'application/pdf'
                };
            });
        } else {
            this.adjuntosSeleccionados = [];
        }

        this.metodosUi.showToast('Borrador cargado correctamente', 'success');
    }

    /**
     * Elimina el borrador de la bitácora cambiando su estado a ELIMINADO.
     */
    async eliminarBorrador(borrador: any, event: Event): Promise<void> {
        event.stopPropagation();

        const confirm = await this.metodosUi.showConfirmation(
            'Eliminar Borrador',
            '¿Estás seguro de que deseas eliminar este borrador?',
            'Eliminar',
            true
        );

        if (!confirm) return;

        try {
            const payload = this.metodosGenService.setModeloGeneral(
                'correos', 'UPDATE_STATUS',
                String(borrador.id), '', '', '', '', 'ELIMINADO', '', '', '', '', '', '', '', '', ''
            );
            const res: any = await firstValueFrom(
                this.javapiService.getCommonModel(payload).pipe(timeout(10000))
            );
            if (Array.isArray(res) && res[0] && res[0].dt1 === 'OK') {
                this.metodosUi.showToast('Borrador eliminado correctamente', 'success');
                this.cargarBorradoresRegistro();
            } else {
                throw new Error(res[0]?.dt1 || 'Error al eliminar');
            }
        } catch (e: any) {
            console.error('[EnviarCorreo] Error al eliminar borrador:', e);
            this.metodosUi.showToast('No se pudo eliminar el borrador', 'danger');
        }
    }

    /**
     * Alterna la visualización del detalle del correo histórico.
     */
    toggleExpandirCorreo(id: string): void {
        this.correoDetalleAbiertoId = this.correoDetalleAbiertoId === id ? null : id;
    }

    // =============================================================
    // DESTINATARIOS
    // =============================================

    /** Alterna la visibilidad del buscador de destinatarios y hace focus. */
    openAddDestinatario(): void {
        this.mostrarBuscadorDestinatario = !this.mostrarBuscadorDestinatario;
        this.mostrarBuscadorGrupo = false;
        this.busquedaDestinatario = '';
        this.destinatariosFiltrados = [];

        if (this.mostrarBuscadorDestinatario) {
            setTimeout(() => {
                this.searchDest?.nativeElement.focus();
            }, 150);
        }
    }

    /** Filtra la lista de destinatarios por el texto de búsqueda. */
    filtrarDestinatarios(): void {
        const q = this.busquedaDestinatario.toLowerCase().trim();
        if (q.length < 1) {
            this.destinatariosFiltrados = [];
            return;
        }
        // Excluir los ya seleccionados
        const idsSeleccionados = this.destinatariosSeleccionados.map(d => d.id);
        this.destinatariosFiltrados = this.todosDestinatarios.filter(d =>
            !idsSeleccionados.includes(d.id) &&
            (d.nombre.toLowerCase().includes(q) || d.correo.toLowerCase().includes(q))
        );
    }

    /** Agrega un destinatario a la lista de seleccionados con tipo TO por defecto. */
    agregarDestinatario(dest: Destinatario): void {
        const existe = this.destinatariosSeleccionados.some(d => d.id === dest.id);
        if (!existe) {
            this.destinatariosSeleccionados = [...this.destinatariosSeleccionados, { ...dest, tipo: 'TO' }];
        }
        this.busquedaDestinatario = '';
        this.destinatariosFiltrados = [];
        this.mostrarBuscadorDestinatario = false;
    }

    /** Alterna el tipo de un destinatario entre TO y CC. */
    toggleTipoDestinatario(dest: Destinatario): void {
        dest.tipo = dest.tipo === 'TO' ? 'CC' : 'TO';
        this.destinatariosSeleccionados = [...this.destinatariosSeleccionados];
    }

    /** Quita un destinatario de la lista de seleccionados. */
    quitarDestinatario(dest: Destinatario): void {
        this.destinatariosSeleccionados = this.destinatariosSeleccionados.filter(d => d.id !== dest.id);
    }

    // =============================================
    // GRUPOS
    // =============================================

    /** Alterna la visibilidad del buscador de grupos y hace focus. */
    openAddGrupo(): void {
        this.mostrarBuscadorGrupo = !this.mostrarBuscadorGrupo;
        this.mostrarBuscadorDestinatario = false;
        this.busquedaGrupo = '';
        this.gruposFiltrados = [];

        if (this.mostrarBuscadorGrupo) {
            setTimeout(() => {
                this.searchGroup?.nativeElement.focus();
            }, 150);
        }
    }

    /** Filtra la lista de grupos por el texto de búsqueda. */
    filtrarGrupos(): void {
        const q = this.busquedaGrupo.toLowerCase().trim();
        if (q.length < 1) {
            this.gruposFiltrados = [];
            return;
        }
        const idsSeleccionados = this.gruposSeleccionados.map(g => g.id);
        this.gruposFiltrados = this.todosGrupos.filter(g =>
            !idsSeleccionados.includes(g.id) &&
            g.nombre.toLowerCase().includes(q)
        );
    }

    /** Agrega un grupo a la lista de seleccionados con tipo TO por defecto. */
    agregarGrupo(grupo: GrupoCorreo): void {
        const existe = this.gruposSeleccionados.some(g => g.id === grupo.id);
        if (!existe) {
            this.gruposSeleccionados = [...this.gruposSeleccionados, { ...grupo, tipo: 'TO' }];
        }
        this.busquedaGrupo = '';
        this.gruposFiltrados = [];
        this.mostrarBuscadorGrupo = false;
    }

    /** Alterna el tipo de un grupo entre TO y CC. */
    toggleTipoGrupo(grupo: GrupoCorreo): void {
        grupo.tipo = grupo.tipo === 'TO' ? 'CC' : 'TO';
        this.gruposSeleccionados = [...this.gruposSeleccionados];
    }

    /** Quita un grupo de la lista de seleccionados. */
    quitarGrupo(grupo: GrupoCorreo): void {
        this.gruposSeleccionados = this.gruposSeleccionados.filter(g => g.id !== grupo.id);
    }

    // =============================================
    // ADJUNTOS
    // =============================================

    /**
     * Maneja la selección de archivos desde el input file.
     * Permite múltiples archivos y evita duplicados por nombre.
     */
    onFilesSelected(event: any): void {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) return;

        const nombresExistentes = this.adjuntosSeleccionados.map(a => a.nombre);

        Array.from(files).forEach((file: File) => {
            if (!nombresExistentes.includes(file.name)) {
                this.adjuntosSeleccionados = [...this.adjuntosSeleccionados, {
                    nombre: file.name,
                    tamanio: file.size,
                    tipo: file.type || 'application/octet-stream',
                    archivo: file
                }];
            }
        });

        // Limpiar el input para permitir re-selección del mismo archivo
        event.target.value = '';
    }

    /** Quita un adjunto de la lista por su índice. */
    quitarAdjunto(index: number): void {
        const adjunto = this.adjuntosSeleccionados[index];
        if (adjunto && adjunto.esRutaServidor) {
            if (adjunto.rutaServidor === this.rutaArs) {
                this.adjuntarArs = false;
            } else if (adjunto.rutaServidor === this.rutaInft) {
                this.adjuntarInft = false;
            } else if (adjunto.rutaServidor === this.rutaCotizacion) {
                this.adjuntarCotizacion = false;
            }
        }
        this.adjuntosSeleccionados = this.adjuntosSeleccionados.filter((_, i) => i !== index);
    }

    /**
     * Abre el visor para un archivo adjunto.
     * Si es del servidor, usa la URL de Java. Si es local, usa un URL temporal.
     */
    async verAdjunto(adjunto: AdjuntoCorreo): Promise<void> {
        try {
            if (adjunto.esRutaServidor) {
                let url = '';
                if (this.moduloOrigen === 'ARS') {
                    if (adjunto.rutaServidor === this.rutaArs) {
                        url = this.javapiService.getFileUrl('ARS', this.idReferencia, false);
                    } else if (adjunto.rutaServidor === this.rutaInft) {
                        url = this.javapiService.getFileUrl('ITF', this.idReferencia, false);
                    } else if (adjunto.rutaServidor === this.rutaCotizacion) {
                        const match = this.rutaCotizacion.match(/(\d+)\.pdf$/);
                        const cotizacionId = this.cotizacionId || (match ? match[1] : '');
                        if (cotizacionId) {
                            url = this.javapiService.getFileUrl('COT', cotizacionId, false);
                        } else {
                            this.metodosUi.showToast('No se encontró el ID de la cotización', 'warning');
                            return;
                        }
                    }
                } else if (this.moduloOrigen === 'COTIZACIONES') {
                    url = this.javapiService.getFileUrl('COT', this.idReferencia, false);
                }

                if (url) {
                    await Browser.open({ url });
                } else {
                    this.metodosUi.showToast('La visualización directa no está disponible para este archivo', 'info');
                }
            } else if (adjunto.archivo) {
                // Para archivos locales recién seleccionados
                const url = URL.createObjectURL(adjunto.archivo);
                await Browser.open({ url });
                // Limpiar el objeto URL después de un tiempo para liberar memoria
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
        } catch (e) {
            console.error('[EnviarCorreo] Error al abrir adjunto:', e);
            this.metodosUi.showToast('No se pudo abrir el archivo', 'danger');
        }
    }

    /** Formatea el tamaño del archivo a una cadena legible (KB, MB). */
    formatFileSize(bytes?: number): string {
        if (!bytes || bytes === 0) return '0 KB';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /** Retorna el icono de Ionic correspondiente según el tipo MIME del archivo. */
    getFileIcon(tipo?: string): string {
        if (!tipo) return 'document-outline';
        if (tipo.includes('pdf')) return 'document-text-outline';
        if (tipo.includes('image')) return 'image-outline';
        if (tipo.includes('word') || tipo.includes('document')) return 'document-outline';
        if (tipo.includes('sheet') || tipo.includes('excel') || tipo.includes('csv')) return 'grid-outline';
        if (tipo.includes('zip') || tipo.includes('rar') || tipo.includes('compressed')) return 'archive-outline';
        return 'attach-outline';
    }
    // =============================================
    // TEXTAREA AUTO-RESIZE
    // =============================================

    /** Ajusta automáticamente la altura del textarea al contenido. */
    autoResize(event: any): void {
        const textarea = event.target;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    }

    // =============================================
    // COMPUTED / GETTERS
    // =============================================

    /** Calcula el total de contactos (individuales + de grupos, sin duplicados estimados). */
    get totalContactos(): number {
        const totalGrupos = this.gruposSeleccionados.reduce((sum, g) => sum + g.cantidad, 0);
        return this.destinatariosSeleccionados.length + totalGrupos;
    }

    /**
     * Valida que el formulario tenga los campos mínimos para enviar.
     * Se requiere: al menos 1 destinatario/grupo, asunto, y cuerpo.
     */
    isFormValid(): boolean {
        const tieneDestinatarios =
            this.destinatariosSeleccionados.length > 0 ||
            this.gruposSeleccionados.length > 0;
        return tieneDestinatarios && this.asunto.trim().length > 0 && this.cuerpo.trim().length > 0;
    }

    // =============================================
    // ACCIONES PRINCIPALES
    // =============================================

    /**
     * Guarda el correo como borrador.
     */
    async guardarBorrador(): Promise<void> {
        if (this.isSaving) return;
        this.accionActual = 'GUARDAR';
        this.isSaving = true;

        try {
            const payload = this.prepararPayload('GUARDADO');
            const res: any = await firstValueFrom(this.javapiService.genericPost('api/pg/correos/borrador', payload));

            if (res && res.status === 'success') {
                this.metodosUi.showToast('Correo guardado como borrador', 'success');
                this.modalCtrl.dismiss({ accion: 'GUARDAR', exito: true });
            } else {
                throw new Error(res?.message || 'Error desconocido');
            }
        } catch (e: any) {
            console.error('[EnviarCorreo] Error guardando borrador:', e);
            this.metodosUi.showToast('Error: ' + (e.message || 'Error al guardar'), 'danger');
        } finally {
            this.isSaving = false;
            this.accionActual = null;
        }
    }

    /**
     * Envía el correo usando el backend de Java (Microsoft Graph).
     */
    async enviarCorreo(): Promise<void> {
        if (this.isSaving || !this.isFormValid()) return;
        this.accionActual = 'ENVIAR';
        this.isSaving = true;

        try {
            const payload = this.prepararPayload('ENVIADO');
            const res: any = await firstValueFrom(
                this.javapiService.genericPost('api/pg/correos/enviar', payload).pipe(timeout(25000))
            );

            if (res && res.status === 'success') {
                this.metodosUi.showToast('Correo enviado correctamente', 'success');
                this.modalCtrl.dismiss({ accion: 'ENVIAR', exito: true });
            } else {
                throw new Error(res?.message || 'Error al enviar correo');
            }
        } catch (e: any) {
            console.error('[EnviarCorreo] Error enviando correo:', e);
            this.metodosUi.showToast('Error: ' + (e.message || 'No se pudo enviar el correo'), 'danger');
        } finally {
            this.isSaving = false;
            this.accionActual = null;
        }
    }

    /**
     * Prepara el objeto Modelogeneral para enviar al backend.
     */
    private prepararPayload(estado: 'GUARDADO' | 'ENVIADO'): any {
        // Separar destinatarios individuales en TO y CC
        const correosTo = [
            ...this.destinatariosSeleccionados.filter(d => d.tipo === 'TO').map(d => d.correo),
            ...this.gruposSeleccionados.filter(g => g.tipo === 'TO').reduce((acc: string[], g) => acc.concat(g.correos), [])
        ].join(',');

        const correosCc = [
            ...this.destinatariosSeleccionados.filter(d => d.tipo === 'CC').map(d => d.correo),
            ...this.gruposSeleccionados.filter(g => g.tipo === 'CC').reduce((acc: string[], g) => acc.concat(g.correos), [])
        ].join(',');

        // JSON de detalle para la bitácora (destinatarios individuales)
        const destinatariosDetalleJson = JSON.stringify(
            this.destinatariosSeleccionados.map((d: Destinatario) => ({ id: d.id, nombre: d.nombre, correo: d.correo }))
        );

        // JSON de grupos para la bitácora
        const gruposJson = JSON.stringify(
            this.gruposSeleccionados.map((g: GrupoCorreo) => ({ id: g.id, nombre: g.nombre, cantidad: g.cantidad }))
        );

        // JSON de adjuntos para la bitácora y para el backend
        // Java espera un objeto {"nombre": "ruta/al/archivo", "alias": "nombre_amigable.pdf"}
        const adjuntosParaEnvio = this.adjuntosSeleccionados.map((a: AdjuntoCorreo) => {
            return {
                nombre: a.esRutaServidor ? a.rutaServidor : a.nombre,
                alias: a.alias || a.nombre
            };
        });

        const adjuntosJson = JSON.stringify(adjuntosParaEnvio);

        // Para la bitácora guardamos un poco más de info (opcional)
        const adjuntosBitacoraJson = JSON.stringify(
            this.adjuntosSeleccionados.map((a: AdjuntoCorreo) => ({
                nombre: a.nombre,
                tamanio: a.tamanio || 0,
                tipo: a.tipo || 'application/pdf',
                auto: a.esRutaServidor || false
            }))
        );

        const metadataJson = this.esSolicitud
            ? JSON.stringify({
                items: this.solicitudItems,
                fecha: this.solicitudFechaISO,
                hora: this.solicitudHora,
                minutos: this.solicitudMinutos,
                ampm: this.solicitudAmPm,
                trabajadores: this.solicitudTrabajadores.map(t => ({ id: t.id, nombre: t.nombre, cedula: t.cedula }))
              })
            : '';

        // Si es solicitud, inyectar la tabla HTML real de trabajadores en el cuerpo final a enviar y registrar
        let cuerpoFinal = this.cuerpo;
        if (this.esSolicitud) {
            const tablaHtml = this.generarTablaPersonalHtml();
            cuerpoFinal = cuerpoFinal.replace(/\$tabla_personal/g, tablaHtml);
        }

        return this.metodosGenService.setModeloGeneral(
            'correos',
            'INSERT',
            this.asunto,
            cuerpoFinal,
            correosTo,           // dt3 -> Lista de correos TO
            gruposJson,          // dt4 -> grupos JSON
            adjuntosJson,        // dt5 -> adjuntos JSON (RUTAS PARA EL JAVA)
            estado,              // dt6
            this.moduloOrigen,   // dt7
            String(this.idReferencia), // dt8
            this.usuarioActual,  // dt9
            destinatariosDetalleJson, // dt10 -> Detalle de destinatarios para la bitácora
            adjuntosBitacoraJson,     // dt11 -> Detalle de adjuntos para la bitácora
            correosCc,               // dt12 -> Lista de correos CC
            metadataJson,            // dt13 -> metadata JSON para la bitácora
            '', this.aliasRemitente  // dt14, dt15
        );
    }

    /** Cierra el modal sin realizar ninguna acción. */
    dismiss(): void {
        this.modalCtrl.dismiss({ accion: null });
    }

    // =============================================
    // SOLICITUD - CALENDARIO SEMANAL PREMIUM
    // =============================================

    /** Devuelve el lunes de la semana a la que pertenece una fecha. */
    getSolicitudMonday(date: Date): Date {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    /** Formatea una Date como 'YYYY-MM-DD' local. */
    solicitudToLocalISO(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    /** Retorna los 7 días de la semana visible. */
    get solicitudWeekDays(): Date[] {
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(this.solicitudWeekStart);
            d.setDate(d.getDate() + i);
            return d;
        });
    }

    /** Etiqueta de rango semanal: "02 jun – 08 jun" */
    get solicitudWeekLabel(): string {
        const end = new Date(this.solicitudWeekStart);
        end.setDate(end.getDate() + 6);
        const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
        return `${this.solicitudWeekStart.toLocaleDateString('es-ES', opts)} – ${end.toLocaleDateString('es-ES', opts)}`;
    }

    isSolicitudToday(date: Date): boolean {
        return this.solicitudToLocalISO(date) === this.solicitudToLocalISO(new Date());
    }

    isSolicitudSelected(date: Date): boolean {
        return this.solicitudToLocalISO(date) === this.solicitudFechaISO;
    }

    solicitudPrevWeek(): void {
        const d = new Date(this.solicitudWeekStart);
        d.setDate(d.getDate() - 7);
        this.solicitudWeekStart = d;
    }

    solicitudNextWeek(): void {
        const d = new Date(this.solicitudWeekStart);
        d.setDate(d.getDate() + 7);
        this.solicitudWeekStart = d;
    }

    /** Selecciona un día del calendario y reprocesa el template. */
    seleccionarDiaSolicitud(date: Date): void {
        this.solicitudFechaISO = this.solicitudToLocalISO(date);
        this.solicitudFechaTexto = this.formatearFechaEspanol(this.solicitudFechaISO);
        this.reprocesarPlantillaSolicitud();
    }

    /**
     * Convierte 'YYYY-MM-DD' a formato largo en español:
     * "lunes 01 de junio 2026"
     */
    formatearFechaEspanol(isoDate: string): string {
        if (!isoDate) return '';
        const parts = isoDate.split('-');
        if (parts.length !== 3) return isoDate;
        const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        const opts: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        };
        const formatted = date.toLocaleDateString('es-ES', opts);
        // Capitalizar primer letra
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }

    /** Retorna la cadena de hora formateada, ej: "8:30 AM" */
    get solicitudHoraTexto(): string {
        return `${this.solicitudHora}:${this.solicitudMinutos} ${this.solicitudAmPm}`;
    }

    /** Llamar cada vez que cambia hora, minutos o AM/PM */
    onSolicitudTiempoChange(): void {
        this.reprocesarPlantillaSolicitud();
    }

    /** Agrega un nuevo ítem de trabajo a la lista. */
    agregarItemSolicitud(): void {
        const texto = this.nuevoItemTrabajo.trim();
        if (!texto) return;
        this.solicitudItems = [...this.solicitudItems, texto];
        this.nuevoItemTrabajo = '';
        this.reprocesarPlantillaSolicitud();
    }

    /** Elimina un ítem por índice. */
    eliminarItemSolicitud(index: number): void {
        this.solicitudItems = this.solicitudItems.filter((_, i) => i !== index);
        this.reprocesarPlantillaSolicitud();
    }

    /**
     * Abre el modal para seleccionar trabajadores en modo 'select'.
     */
    async abrirSelectorTrabajadores(): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: WorkerSelectorComponent,
            componentProps: {
                trabajo: this.dataSource,
                mode: 'select',
                existingIds: this.solicitudTrabajadores.map(t => t.id)
            },
            cssClass: 'fullscreen-modal'
        });
        await modal.present();
        const res = await modal.onWillDismiss();
        if (res?.data?.saved && Array.isArray(res.data.workers)) {
            const nuevos = res.data.workers.filter(
                (w: any) => !this.solicitudTrabajadores.some(st => String(st.id) === String(w.id))
            );
            this.solicitudTrabajadores = [...this.solicitudTrabajadores, ...nuevos];
            this.reprocesarPlantillaSolicitud();
        }
    }

    /**
     * Quita un trabajador del listado de solicitud.
     */
    eliminarTrabajadorSolicitud(index: number): void {
        this.solicitudTrabajadores = this.solicitudTrabajadores.filter((_, i) => i !== index);
        this.reprocesarPlantillaSolicitud();
    }

    /**
     * Genera la tabla HTML de personal para enviar en el correo.
     */
    generarTablaPersonalHtml(): string {
        let tablaPersonalHtml = '';
        if (this.solicitudTrabajadores.length > 0) {
            tablaPersonalHtml = `
<table style="width:100%; max-width: 600px; border-collapse: collapse; margin-top: 15px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 13px; color: #ffffff; border: 1px solid #1a1a1a;">
  <thead>
    <tr style="background-color: #b83a24; color: #ffffff; text-align: center;">
      <th style="padding: 10px 14px; font-weight: bold; border: 1px solid #444444;">Nombre</th>
      <th style="padding: 10px 14px; font-weight: bold; border: 1px solid #444444; width: 180px;">Cédula</th>
    </tr>
  </thead>
  <tbody>`;
            this.solicitudTrabajadores.forEach((t) => {
                tablaPersonalHtml += `
    <tr style="background-color: #2b2b2b; border-bottom: 1px solid #444444; text-align: center;">
      <td style="padding: 10px 14px; border-right: 1px solid #444444; color: #ffffff;">${t.nombre}</td>
      <td style="padding: 10px 14px; color: #ffffff;">${t.cedula || 'N/A'}</td>
    </tr>`;
            });
            tablaPersonalHtml += `
  </tbody>
</table>`;
            // Eliminar saltos de línea y espacios entre tags para evitar que el backend inserte etiquetas <br>
            tablaPersonalHtml = tablaPersonalHtml.replace(/\r?\n/g, '').replace(/>\s+</g, '><').trim();
        }
        return tablaPersonalHtml;
    }

    /**
     * Reprocesa asunto y cuerpo con los valores actuales de fecha, hora e ítems.
     * Se invoca cada vez que cambia cualquier control de solicitud.
     */
    reprocesarPlantillaSolicitud(): void {
        if (!this.esSolicitud) return;

        const itemsTexto = this.solicitudItems.length > 0
            ? this.solicitudItems.map(item => `• ${item}`).join('\n')
            : '(Sin ítems de trabajo)';

        const dataConSolicitud = {
            ...this.dataSource,
            fecha: this.solicitudFechaISO, // usamos el ISO temporal o formateado
            hora: this.solicitudHoraTexto,
            items_trabajos: itemsTexto,
            // En el editor de texto visible, mantenemos la variable $tabla_personal limpia
            tabla_personal: '$tabla_personal'
        };

        // Corrección de fecha en español largo
        dataConSolicitud.fecha = this.solicitudFechaTexto || '(Sin fecha)';

        if (this.rawAsuntoTemplate) {
            this.asunto = this.metodosGenService.processTemplate(this.rawAsuntoTemplate, dataConSolicitud);
        }
        if (this.rawCuerpoTemplate) {
            this.cuerpo = this.metodosGenService.processTemplate(this.rawCuerpoTemplate, dataConSolicitud);
        }
    }

    /** Maneja Enter en el input de ítems de solicitud. */
    onItemKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.agregarItemSolicitud();
        }
    }
}
